import { roleAtLeast, type HealthStatus } from '@ai-sales/shared'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { enqueueDiagnostics, fetchHealth, fetchQueueStats } from '../api/workspace.api.js'
import { ROLE_LABELS } from '../auth/require-auth.js'
import { Badge, StatusDot, type Tone } from '../components/badge.js'
import { Button } from '../components/button.js'
import { ErrorPanel, InlineError } from '../components/error-panel.js'
import { PageHeader, Panel, Stat } from '../components/page.js'
import { Spinner } from '../components/spinner.js'
import { formatTime, formatUptime } from '../lib/format.js'
import { useCurrentWorkspace } from '../workspace/workspace-context.js'

const STATUS_LABEL: Record<HealthStatus, string> = {
  ok: 'Работает',
  degraded: 'Частично',
  down: 'Недоступно',
}

const STATUS_TONE: Record<HealthStatus, Tone> = {
  ok: 'ok',
  degraded: 'warn',
  down: 'bad',
}

/**
 * Экран состояния — первое, что видит человек, и первое, куда он придёт,
 * когда что-то работает не так.
 *
 * Поэтому здесь не приветствие и не сводка по продажам (её нечем наполнить
 * до этапа 4), а ответ на вопрос «система вообще жива?»: зависимости,
 * очередь задач и роль текущего пользователя.
 */
export function OverviewPage() {
  const workspace = useCurrentWorkspace()
  const canSeeQueue = roleAtLeast(workspace.role, 'admin')

  const health = useQuery({
    queryKey: ['health'],
    queryFn: fetchHealth,
    refetchInterval: 15_000,
  })

  const queue = useQuery({
    queryKey: ['queue-stats', workspace.id],
    queryFn: () => fetchQueueStats(workspace.id),
    refetchInterval: 15_000,
    enabled: canSeeQueue,
  })

  return (
    <>
      <PageHeader
        title="Состояние"
        description={`Воркспейс «${workspace.name}», ваша роль — ${ROLE_LABELS[workspace.role]}.`}
        actions={
          <Button
            size="sm"
            loading={health.isFetching || queue.isFetching}
            onClick={() => {
              void health.refetch()
              if (canSeeQueue) void queue.refetch()
            }}
          >
            Обновить
          </Button>
        }
      />

      <div className="flex flex-col gap-6">
        {(health.data?.warnings ?? []).length > 0 && (
          <Panel
            title="Работает не всё"
            description="Система на ходу, но эти настройки сейчас бездействуют. Снаружи это не видно никак — поэтому написано здесь."
          >
            <ul className="flex flex-col gap-3">
              {health.data?.warnings.map((warning) => (
                <li
                  key={warning.code}
                  className="rounded-md border border-copper/40 bg-copper/5 px-3 py-2.5"
                >
                  <p className="text-sm text-ink">{warning.text}</p>
                  <p className="mt-1 text-xs text-muted">{warning.fix}</p>
                </li>
              ))}
            </ul>
          </Panel>
        )}

        <Panel
          title="Зависимости"
          description="Проверка выполняется на сервере по запросу и обновляется каждые 15 секунд"
          actions={
            health.data && (
              <Badge tone={STATUS_TONE[health.data.status]}>
                {STATUS_LABEL[health.data.status]}
              </Badge>
            )
          }
        >
          {health.isPending && (
            <p className="flex items-center gap-2 text-sm text-muted">
              <Spinner /> Проверяем…
            </p>
          )}

          {health.error && <ErrorPanel error={health.error} onRetry={() => void health.refetch()} />}

          {health.data && (
            <>
              <ul className="flex flex-col gap-2">
                {health.data.dependencies.map((dependency) => (
                  <li
                    key={dependency.name}
                    className="flex flex-wrap items-center gap-x-3 gap-y-1 rounded-md border border-line-soft bg-raised px-3 py-2"
                  >
                    <StatusDot tone={STATUS_TONE[dependency.status]} />
                    <span className="font-mono text-sm text-ink">{dependency.name}</span>
                    <span className="text-sm text-muted">{STATUS_LABEL[dependency.status]}</span>
                    {dependency.latencyMs !== null && (
                      <span className="text-xs text-muted">{dependency.latencyMs} мс</span>
                    )}
                    {dependency.error && (
                      <span className="w-full text-xs text-crimson">{dependency.error}</span>
                    )}
                  </li>
                ))}
              </ul>

              <p className="mt-3 text-xs text-muted">
                Версия {health.data.version} · в работе {formatUptime(health.data.uptimeSeconds)} ·
                проверено в {formatTime(health.data.checkedAt)}
              </p>
            </>
          )}
        </Panel>

        {canSeeQueue && (
          <Panel
            title="Очередь задач"
            description="Фоновая работа: рассылки, follow-up, доставка конверсий"
          >
            {queue.isPending && (
              <p className="flex items-center gap-2 text-sm text-muted">
                <Spinner /> Считаем…
              </p>
            )}

            {queue.error && <InlineError error={queue.error} />}

            {queue.data && (
              <>
                <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
                  <Stat label="Ожидают" value={queue.data.waiting} />
                  <Stat label="В работе" value={queue.data.active} />
                  <Stat label="Отложены" value={queue.data.delayed} />
                  <Stat
                    label="Упали"
                    value={queue.data.failed}
                    tone={queue.data.failed > 0 ? 'bad' : 'neutral'}
                  />
                  <Stat label="Выполнены" value={queue.data.completed} />
                </div>

                {queue.data.paused && (
                  <p className="mt-3 text-sm text-copper">
                    Очередь на паузе — новые задачи копятся, но не исполняются.
                  </p>
                )}
              </>
            )}
          </Panel>
        )}

        {workspace.role === 'owner' && <DiagnosticsPanel workspaceId={workspace.id} />}
      </div>
    </>
  )
}

/**
 * Проверка цепочки повторов на живой системе.
 *
 * Доступна только владельцу и только если на сервере включён флаг
 * ENABLE_DIAGNOSTICS_JOBS. Смысл кнопки — увидеть своими глазами, что
 * упавшая задача действительно перебирает попытки и попадает в журнал
 * сбоев, а не поверить в это по описанию.
 */
function DiagnosticsPanel({ workspaceId }: { workspaceId: string }) {
  const queryClient = useQueryClient()
  const [lastJobId, setLastJobId] = useState<string | null>(null)

  const enqueue = useMutation({
    mutationFn: (behaviour: 'ok' | 'fail') =>
      enqueueDiagnostics(workspaceId, {
        behaviour,
        delayMs: 0,
        note: 'проверка из интерфейса',
      }),
    onSuccess: (job) => {
      setLastJobId(job.jobId)
      void queryClient.invalidateQueries({ queryKey: ['queue-stats', workspaceId] })
    },
  })

  return (
    <Panel
      title="Диагностика очереди"
      description="Ставит задачу, которая падает намеренно: так видно, что повторы и журнал сбоев работают"
    >
      <div className="flex flex-wrap items-center gap-2">
        <Button
          variant="secondary"
          size="sm"
          loading={enqueue.isPending}
          onClick={() => enqueue.mutate('ok')}
        >
          Успешная задача
        </Button>
        <Button
          variant="danger"
          size="sm"
          loading={enqueue.isPending}
          onClick={() => enqueue.mutate('fail')}
        >
          Падающая задача
        </Button>
      </div>

      {lastJobId && !enqueue.error && (
        <p className="mt-3 text-sm text-muted">
          Задача поставлена, номер <code className="font-mono">{lastJobId}</code>. Падающая
          переберёт попытки примерно за полминуты и появится в разделе «Сбои задач».
        </p>
      )}

      {enqueue.error && <ErrorPanel className="mt-3" error={enqueue.error} />}
    </Panel>
  )
}
